import { apiClient, getBaseUrl, getIdToken } from ".";

export type AuthenticationFactorType = "totp" | "passkey";
// A sign-in method can also be the password, or the emailed recovery code.
// Neither is an enrolled account-security factor.
export type LoginMethodType = AuthenticationFactorType | "password" | "email";

export interface AuthenticationFactor {
    id: string;
    type: AuthenticationFactorType;
    label?: string;
    createdAt?: string;
    lastUsedAt?: string;
}

export interface LoginMethod {
    id: LoginMethodType;
    type: LoginMethodType;
    label: string;
}

export interface SecurityPreferences {
    promptDismissed: boolean;
}

export interface SecurityOverview {
    authenticationFactors: AuthenticationFactor[];
    preferences: SecurityPreferences;
    federated: boolean;
    federatedProviders: string[];
    totpAvailable: boolean;
    passkeysAvailable: boolean;
    totpEnabled: boolean;
    shouldPrompt: boolean;
}

export interface TotpEnrollment {
    transactionId: string;
    expiresAt?: string;
    secret?: string;
    otpauthUri?: string;
}

export interface PublicKeyCredentialCreationOptionsJson {
    challenge: string;
    rp: PublicKeyCredentialRpEntity;
    user: Omit<PublicKeyCredentialUserEntity, "id"> & { id: string };
    pubKeyCredParams: PublicKeyCredentialParameters[];
    timeout?: number;
    excludeCredentials?: Array<Omit<PublicKeyCredentialDescriptor, "id"> & { id: string }>;
    authenticatorSelection?: AuthenticatorSelectionCriteria;
    attestation?: AttestationConveyancePreference;
    extensions?: AuthenticationExtensionsClientInputs;
}

export interface PasskeyRegistration {
    transactionId: string;
    expiresAt?: string;
    publicKey: PublicKeyCredentialCreationOptionsJson;
}

export interface LoginChallenge {
    transactionId: string;
    challengeName: string;
    username?: string;
    expiresAt?: string;
    availableFactors?: LoginMethod[];
    publicKey?: {
        challenge: string;
        timeout?: number;
        rpId?: string;
        allowCredentials?: Array<Omit<PublicKeyCredentialDescriptor, "id"> & { id: string }>;
        userVerification?: UserVerificationRequirement;
    };
}

const METHOD_FOR_CHALLENGE: Record<string, LoginMethod> = {
    SOFTWARE_TOKEN_MFA: { id: "totp", type: "totp", label: "Authenticator app" },
    WEB_AUTHN: { id: "passkey", type: "passkey", label: "Passkey" },
    PASSWORD: { id: "password", type: "password", label: "Password" },
    EMAIL_OTP: { id: "email", type: "email", label: "Email code" }
};

/** The single method a direct (non-selection) Cognito challenge asks for. */
export function loginMethodForChallenge(challengeName?: string): LoginMethod | undefined {
    return challengeName ? METHOD_FOR_CHALLENGE[challengeName] : undefined;
}

export function toLoginChallenge(response: any, username?: string): LoginChallenge | undefined {
    // The server never returns a Cognito Session, so there is no session token to fall back to.
    const transactionId = response?.transactionId;
    if (!transactionId || response?.done) {
        return undefined;
    }

    // Only advertise what Cognito advertised. PASSWORD_SRP and other unsupported
    // names are dropped rather than invented into a choice the server would refuse.
    const availableFactors: LoginMethod[] | undefined = response.availableFactors
        || response.availableChallenges?.reduce((methods: LoginMethod[], name: string) => {
            const method = METHOD_FOR_CHALLENGE[name];
            if (method && !methods.some(existing => existing.type === method.type)) {
                methods.push(method);
            }
            return methods;
        }, []);

    return {
        transactionId,
        challengeName: response.challengeName,
        username: response.username || username,
        expiresAt: response.expiresAt,
        availableFactors,
        publicKey: response.publicKey
    };
}

export type CredentialPayload = Record<string, unknown>;

export function securityErrorCode(error: any): string | undefined {
    return error?.response?.data?.code;
}

export function securityErrorStatus(error: any): number | undefined {
    return error?.response?.status;
}

function getCognitoAccessToken() {
    return localStorage.getItem("accessToken") || undefined;
}

function requestHeaders() {
    const idToken = getIdToken();
    const accessToken = getCognitoAccessToken();

    return {
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...(accessToken ? { "X-Cognito-Access-Token": accessToken } : {})
    };
}

async function request<T>(method: "get" | "post" | "put" | "patch" | "delete", path: string, data?: unknown) {
    // Sessions created before the access token was persisted have no stored access
    // token. One refresh backfills it so account-security calls keep working. The
    // interceptor module is loaded lazily so this adapter stays testable in isolation.
    if (!getCognitoAccessToken() && localStorage.getItem("refreshToken")) {
        try {
            const { refreshSession } = await import("./interceptors");
            await refreshSession();
        } catch {
            // The request below fails with the usual unauthorized handling.
        }
    }

    const response = await apiClient.request({
        method,
        url: `${getBaseUrl()}/ms-usermanagement${path}`,
        data,
        headers: requestHeaders()
    });

    return response.data as T;
}

function toFactor(input: any): AuthenticationFactor {
    return {
        id: input.id,
        type: input.type,
        label: input.label || input.name,
        createdAt: input.createdAt,
        lastUsedAt: input.lastUsedAt
    };
}

function toPreferences(input: any): SecurityPreferences {
    return { promptDismissed: input?.promptDismissed === true };
}

/**
 * These request types deliberately live outside the generated user-management
 * SDK. The server contract is new and the SDK can replace this adapter when it
 * is published without changing the account-security UI.
 */
export const securityApi = {
    getOverview: async () => {
        const overview = await request<any>("get", "/users/me/security-overview");
        return {
            authenticationFactors: (overview.factors || []).map(toFactor),
            preferences: toPreferences(overview.preferences),
            federated: Boolean(overview.federated),
            federatedProviders: overview.federatedProviders || [],
            totpAvailable: overview.totpAvailable === true,
            passkeysAvailable: overview.passkeysAvailable === true,
            totpEnabled: overview.totpEnabled === true,
            shouldPrompt: overview.shouldPrompt === true
        } as SecurityOverview;
    },

    /** Identifier-first sign-in: learn which methods an account offers before asking for a credential. */
    startLogin: (username: string) =>
        request<any>("post", "/users/login-challenges", { username }),

    requestActivation: (username: string) =>
        request<{ done: boolean }>("post", "/users/activation-requests", { username }),

    updatePreferences: async (promptDismissed: boolean) => {
        const result = await request<any>("patch", "/users/me/security-preferences", { promptDismissed });
        return toPreferences(result);
    },

    startTotpEnrollment: () =>
        request<TotpEnrollment>("post", "/users/me/totp-enrollments"),

    verifyTotpEnrollment: (transactionId: string, code: string) =>
        request<{ done?: boolean }>("post", "/users/me/totp-enrollments/verification", { transactionId, code }),

    startPasskeyRegistration: () =>
        request<PasskeyRegistration>("post", "/users/me/passkey-registrations"),

    verifyPasskeyRegistration: (transactionId: string, credential: CredentialPayload, name?: string) =>
        request<{ done?: boolean }>("post", "/users/me/passkey-registrations/verification", { transactionId, credential, name }),

    renameFactor: (factorId: string, name: string) =>
        request<{ done?: boolean }>("patch", `/users/me/authentication-factors/${encodeURIComponent(factorId)}`, { name }),

    removeFactor: (factorId: string) =>
        request<void>("delete", `/users/me/authentication-factors/${encodeURIComponent(factorId)}`),

    respondToLoginChallenge: (transactionId: string, response: { code?: string; newPassword?: string; credential?: CredentialPayload; factorId?: string; password?: string }) =>
        request<any>("post", "/users/login-challenges/responses", { transactionId, ...response })
};
